import { useState } from 'react';

import { Button } from 'primereact/button';
import { confirmDialog } from 'primereact/confirmdialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { useTranslation } from 'react-i18next';

import {
  useCreateAnnotation,
  useDeleteAnnotation,
  useGetEmbryoAnnotations,
} from '@/services/embryo-service/hooks';
import { parseWallClock } from '@/utils/wallClock';

interface EmbryoNotesTabProps {
  embryoId: string | undefined;
}

export function EmbryoNotesTab({ embryoId }: EmbryoNotesTabProps) {
  const { t } = useTranslation();
  const [noteText, setNoteText] = useState('');
  const { data: annotations, isLoading } = useGetEmbryoAnnotations(embryoId);
  const createAnnotation = useCreateAnnotation();
  const deleteAnnotation = useDeleteAnnotation();

  const handleAddNote = () => {
    if (!embryoId || !noteText.trim()) return;

    createAnnotation.mutate(
      { embryoId, noteText: noteText.trim() },
      {
        onSuccess: () => {
          setNoteText('');
        },
      },
    );
  };

  const handleDeleteNote = (annotationId: string) => {
    if (!embryoId) return;

    confirmDialog({
      message: t('embryo.notesDeleteConfirm', {
        defaultValue: 'Are you sure you want to delete this note?',
      }),
      header: t('common.confirm', { defaultValue: 'Confirm' }),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: () => {
        deleteAnnotation.mutate({ embryoId, annotationId });
      },
    });
  };
  if (isLoading) {
    return <div className="p-4">{t('common.loading')}...</div>;
  }

  return (
    <div className="notes-tab p-2">
      <div className="mb-4">
        <div className="flex flex-col gap-2">
          <InputTextarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            rows={3}
            className="w-full"
            placeholder={t('embryo.notesPlaceholder')}
          />
          <Button
            label={t('embryo.notesAddNote')}
            icon="pi pi-plus"
            onClick={handleAddNote}
            disabled={!noteText.trim() || createAnnotation.isPending}
            loading={createAnnotation.isPending}
            className="w-fit self-start"
          />
        </div>
      </div>

      <div className="space-y-2">
        {annotations && annotations.length > 0 ? (
          annotations.map((annotation) => (
            <div
              key={annotation.id}
              className="surface-border border-bottom-1 px-1 py-2"
            >
              <div className="flex items-start justify-between">
                <div className="text-color-secondary mb-1 text-sm">
                  {(annotation.ownerName || annotation.createdByName) && (
                    <span className="mr-2 font-semibold text-blue-600">
                      {annotation.ownerName || annotation.createdByName}
                    </span>
                  )}
                  {annotation.createdOn && (
                    <span>
                      {parseWallClock(annotation.createdOn)?.toLocaleString()}
                    </span>
                  )}
                </div>
                {annotation.isOwner && (
                  <Button
                    icon="pi pi-trash"
                    severity="danger"
                    text
                    rounded
                    size="small"
                    onClick={() => handleDeleteNote(annotation.id)}
                    disabled={deleteAnnotation.isPending}
                    tooltip={t('embryo.notesDeleteNote', {
                      defaultValue: 'Delete note',
                    })}
                    tooltipOptions={{ position: 'left' }}
                  />
                )}
              </div>
              <div className="text-color">{annotation.noteText}</div>
            </div>
          ))
        ) : (
          <div className="text-color-secondary p-4 text-center">
            {t('embryo.notesNoNotes')}
          </div>
        )}
      </div>
    </div>
  );
}
